import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Textarea } from '../ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CITIES = [
  'Hyderabad',
  'Chennai',
  'Bengaluru',
  'Pune',
  'Mumbai',
  'Delhi',
  'Kolkata'
];

const MedicalTestModal = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    city: '',
    appointment_date: '',
    medical_history: false,
    medical_history_details: ''
  });
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    generateAvailableDates();
  }, []);

  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 10; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      dates.push({ date: dateStr, day: dayName });
    }
    
    setAvailableDates(dates);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/medical/appointment`, formData);
      toast.success('Medical test appointment scheduled!');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading font-semibold text-2xl">Visa Medical Test Appointment</DialogTitle>
          <p className="text-muted-foreground">Schedule your medical examination</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="city">Select City</Label>
            <Select value={formData.city} onValueChange={(value) => setFormData({ ...formData, city: value })}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Choose your preferred city" />
              </SelectTrigger>
              <SelectContent>
                {CITIES.map((city) => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="date">Select Date</Label>
            <Select value={formData.appointment_date} onValueChange={(value) => setFormData({ ...formData, appointment_date: value })}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Choose appointment date" />
              </SelectTrigger>
              <SelectContent>
                {availableDates.map((dateObj) => (
                  <SelectItem key={dateObj.date} value={dateObj.date}>
                    {dateObj.date} ({dateObj.day})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <Label>Do you have any ongoing medical history?</Label>
            <RadioGroup
              value={formData.medical_history ? 'yes' : 'no'}
              onValueChange={(value) => setFormData({ ...formData, medical_history: value === 'yes' })}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="no" />
                <Label htmlFor="no" className="font-normal cursor-pointer">No</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="yes" />
                <Label htmlFor="yes" className="font-normal cursor-pointer">Yes</Label>
              </div>
            </RadioGroup>
          </div>

          {formData.medical_history && (
            <div className="space-y-2">
              <Label htmlFor="details">Please provide details</Label>
              <Textarea
                id="details"
                value={formData.medical_history_details}
                onChange={(e) => setFormData({ ...formData, medical_history_details: e.target.value })}
                placeholder="Describe your medical history..."
                rows={4}
                className="rounded-xl"
              />
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || !formData.city || !formData.appointment_date}
            className="w-full rounded-full bg-primary hover:bg-primary/90"
          >
            {loading ? 'Scheduling...' : 'Schedule Appointment'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MedicalTestModal;